// 欄のスクロールバー（PC。段階 C の2 の 7g-3、2026-10-26。前は jQuery の mCustomScrollbar 3.1.5）。
// 見た目はプラグインと同じ DOM・id・class（mCSB_番号・mCSB_番号_container・mCSB_scrollTools・mCSB_dragger など）を作って、
// ページの CSS（css/jquery.mCustomScrollbar.css の mCS-light）がそのまま効くようにしている。番号は作った順（プラグインと同じ。parts.ts が #mCSB_2_container を使う）。
// 動き（2026-10-26 ユーザー決定「同じ見た目・同じ動き（押す・引っぱる・ホイール）」）: ホイールは 1 目盛りで mouseWheelPixels（細かいホイール・タッチパッドは
// その量だけすぐ）、つまみを引っぱると中身があとから追いかける、レールを押すと見えている幅の 9 割ずつ、タッチで中身を引っぱれる、矢印・PageUp/Down・Home/End のキー。
// 中身の大きさの変化は 60ms ごとに見て合わせ直す（プラグインと同じ）。動きの式（mcsEaseOut・mcsEaseInOut）と止まる所の決め方はプラグインと同じ
import * as jq from './jq.js';

export interface ScrollbarOptions {
  axis: 'x' | 'y';
  /** 動きの時間（ms） */
  scrollInertia: number;
  /** ホイールの 1 目盛りで動く量（px） */
  mouseWheelPixels: number;
  /** カーソルが乗っていないときはバーを隠す（CSS の mCS-autoHide） */
  autoHideScrollbar: boolean;
}

type Tweenable = HTMLElement & { _mTween?: Record<'top' | 'left', TweenState>; _draggable?: number; autoUpdate?: number; idleTimer?: number; onCompleteTimeout?: number };
interface TweenState {
  id?: number | null;
  stop?: number;
  time?: number;
}
interface Instance {
  idx: number;
  root: HTMLElement;
  opt: ScrollbarOptions;
  box: HTMLElement;
  container: Tweenable;
  tools: HTMLElement;
  dragger: Tweenable;
  overflowed: [boolean, boolean] | null;
  ratio: { x: number; y: number };
  tweenRunning: boolean;
  bound: boolean;
  disabled: boolean;
  pollSize: number;
}

const instances = new WeakMap<Element, Instance>();
let lastIdx = 0;

// ---- 動き（プラグインの _tweenTo と同じ式・コマの進め方） ----

const now = (): number => performance.now();
function ease(t: number, b: number, c: number, d: number, name: string): number {
  switch (name) {
    case 'linear':
    case 'mcsLinear':
      return (c * t) / d + b;
    case 'mcsLinearOut':
      t /= d;
      t--;
      return c * Math.sqrt(1 - t * t) + b;
    case 'mcsEaseInOut':
      t /= d / 2;
      if (t < 1) return (c / 2) * t * t * t + b;
      t -= 2;
      return (c / 2) * (t * t * t + 2) + b;
    default: {
      // mcsEaseOut
      const ts = (t /= d) * t, tc = ts * t;
      return b + c * (0.499999999999997 * tc * ts + -2.5 * ts * ts + 5.5 * tc + -6.5 * ts + 4 * t);
    }
  }
}
function tweenTo(el: Tweenable, prop: 'top' | 'left', to: number, dur: number, easing: string, overwrite: string, cb: { onStart?: () => void; onUpdate?: () => void; onComplete?: () => void } = {}): void {
  if (!el._mTween) el._mTween = { top: {}, left: {} };
  const st = el._mTween[prop];
  const from = prop === 'left' ? el.offsetLeft : el.offsetTop;
  const change = to - from;
  const t0 = now();
  const frame = 1000 / 60;
  let elapsed = 0;
  st.stop = 0;
  if (overwrite !== 'none') cancel(st);
  st.time = elapsed + frame;
  const step = (): void => {
    if (st.stop) return;
    if (!elapsed && cb.onStart) cb.onStart();
    elapsed = now() - t0;
    el.style[prop] = (dur > 0 ? Math.round(ease(st.time!, from, change, dur, easing)) : to) + 'px';
    if (cb.onUpdate) cb.onUpdate();
    if (elapsed >= st.time!) {
      st.time = elapsed > st.time! ? elapsed + frame - (elapsed - st.time!) : elapsed + frame - 1;
      if (st.time < elapsed + 1) st.time = elapsed + 1;
    }
    if (st.time! < dur) st.id = requestAnimationFrame(step);
    else if (cb.onComplete) cb.onComplete();
  };
  st.id = requestAnimationFrame(step);
}
function cancel(st: TweenState): void {
  if (st.id != null) {
    cancelAnimationFrame(st.id);
    st.id = null;
  }
}
/** その要素の動きを止める */
function stopTweens(el: Tweenable): void {
  if (!el._mTween) el._mTween = { top: {}, left: {} };
  for (const p of ['top', 'left'] as const) {
    const st = el._mTween[p];
    if (st.id) {
      cancel(st);
      st.stop = 1;
    }
  }
}
const stopAll = (s: Instance): void => {
  stopTweens(s.container);
  stopTweens(s.dragger);
};

// ---- 作る ----

/** 欄にスクロールバーを付ける（プラグインの $(sel).mCustomScrollbar(設定)） */
export function init(sel: string, opt: ScrollbarOptions): void {
  for (const root of jq.els(sel)) {
    if (instances.has(root)) continue;
    const idx = ++lastIdx;
    const x = opt.axis === 'x';
    root.classList.add('mCustomScrollbar', '_mCS_' + idx);
    if (opt.autoHideScrollbar) root.classList.add('mCS-autoHide');
    const box = document.createElement('div');
    box.id = 'mCSB_' + idx;
    box.className = 'mCustomScrollBox mCS-light ' + (x ? 'mCSB_horizontal' : 'mCSB_vertical');
    const container = document.createElement('div') as Tweenable;
    container.id = 'mCSB_' + idx + '_container';
    container.className = 'mCSB_container';
    container.setAttribute('style', 'position:relative; top:0; left:0;');
    container.setAttribute('dir', 'ltr');
    while (root.firstChild) container.appendChild(root.firstChild);
    box.appendChild(container);
    root.appendChild(box);
    if (x) jq.setCss(container, 'width', contentWidth(container));
    box.classList.add('mCSB_inside');
    const dir = x ? 'horizontal' : 'vertical';
    const tools = document.createElement('div');
    tools.id = 'mCSB_' + idx + '_scrollbar_' + dir;
    tools.className = 'mCSB_scrollTools mCSB_' + idx + '_scrollbar mCS-light mCSB_scrollTools_' + dir;
    // レールはつまみの中（プラグインは <div … /> で書いていて、jQuery 3.5 からは閉じたタグとして読まれず、レールがつまみの中に入っていた。見た目を前と同じに）
    tools.innerHTML =
      "<div class='mCSB_draggerContainer'><div id='mCSB_" + idx + '_dragger_' + dir + "' class='mCSB_dragger' style='position:absolute;'><div class='mCSB_dragger_bar'></div><div class='mCSB_draggerRail'></div></div></div>";
    box.appendChild(tools);
    const dragger = tools.querySelector('.mCSB_dragger') as Tweenable;
    if (x) jq.setCss(dragger, 'min-width', jq.width(dragger));
    else jq.setCss(dragger, 'min-height', jq.height(dragger));
    const s: Instance = { idx, root, opt, box, container, tools, dragger, overflowed: null, ratio: { x: 0, y: 0 }, tweenRunning: false, bound: false, disabled: false, pollSize: 0 };
    instances.set(root, s);
    update(s);
  }
}

/** 中身の幅（プラグインの _contentWidth: はみ出していればその幅、収まっていれば "100%"） */
function contentWidth(container: HTMLElement): number | string {
  const kids = Array.prototype.slice.call(container.children) as HTMLElement[];
  const widest = kids.length ? Math.max(...kids.map((k) => jq.outerWidth(k, true))) : -Infinity;
  const parentW = jq.width(container.parentElement);
  return container.scrollWidth > parentW ? container.scrollWidth : widest > parentW ? widest : '100%';
}

// ---- 合わせ直す（プラグインの update） ----

function update(s: Instance): void {
  const { root, box, container, opt } = s;
  if (s.tweenRunning) stopAll(s);
  root.classList.remove('mCS_disabled', 'mCS_destroyed');
  s.disabled = false;
  jq.setCss(box, 'max-height', 'none');
  if (jq.height(box) !== jq.height(root)) jq.setCss(box, 'max-height', jq.height(root));
  if (opt.axis === 'x') jq.setCss(container, 'width', contentWidth(container));
  s.overflowed = overflowed(s);
  showScrollbar(s, false);
  draggerLength(s);
  scrollRatio(s);
  bindEvents(s);
  const pos = [Math.abs(container.offsetTop), Math.abs(container.offsetLeft)];
  const i = opt.axis === 'x' ? 1 : 0;
  if (s.overflowed[i]) {
    const tooBig = opt.axis === 'x' ? jq.width(s.dragger) > jq.width(s.dragger.parentElement) : jq.height(s.dragger) > jq.height(s.dragger.parentElement);
    if (tooBig) reset(s);
    else scrollTo(s, pos[i], { dir: opt.axis, dur: 0, overwrite: 'none' });
  } else {
    reset(s);
    s.bound = false;
  }
  syncMarks(s);
  autoUpdate(s, false);
}
function overflowed(s: Instance): [boolean, boolean] {
  const c = s.container;
  let h = s.overflowed == null ? jq.height(c) : jq.outerHeight(c);
  let w = s.overflowed == null ? jq.width(c) : jq.outerWidth(c);
  if (c.scrollHeight > h) h = c.scrollHeight;
  if (c.scrollWidth > w) w = c.scrollWidth;
  return [h > jq.height(s.box), w > jq.width(s.box)];
}
/** バーを出す・隠す（disabled なら隠す） */
function showScrollbar(s: Instance, disabled: boolean): void {
  const x = s.opt.axis === 'x', i = x ? 1 : 0;
  const ov = s.overflowed!;
  if (ov[i] && !disabled) {
    jq.setCss(s.tools, 'display', 'block');
    jq.setCss(s.dragger, 'display', 'block');
    s.container.classList.remove(x ? 'mCS_no_scrollbar_x' : 'mCS_no_scrollbar_y', x ? 'mCS_x_hidden' : 'mCS_y_hidden');
  } else {
    jq.setCss(s.tools, 'display', 'none');
    s.container.classList.add(x ? 'mCS_x_hidden' : 'mCS_y_hidden');
    s.container.classList.add(x ? 'mCS_no_scrollbar_x' : 'mCS_no_scrollbar_y');
  }
  if (ov[0] || ov[1]) s.root.classList.remove('mCS_no_scrollbar');
  else s.root.classList.add('mCS_no_scrollbar');
}
/** つまみの長さ（見えている割合） */
function draggerLength(s: Instance): void {
  const d = s.dragger, parent = d.parentElement!;
  if (s.opt.axis === 'x') {
    jq.setCss(d, 'width', Math.round((jq.width(s.box) / jq.outerWidth(s.container)) * jq.width(parent)));
    jq.setCss(d, 'max-width', jq.width(parent) - 10);
  } else {
    jq.setCss(d, 'height', Math.round((jq.height(s.box) / jq.outerHeight(s.container)) * jq.height(parent)));
    jq.setCss(d, 'max-height', jq.height(parent) - 10);
    jq.setCss(d.querySelector('.mCSB_dragger_bar'), 'line-height', parseInt(jq.css(d, 'min-height')) + 'px');
  }
}
function scrollRatio(s: Instance): void {
  const d = s.dragger, parent = d.parentElement!;
  s.ratio = {
    y: (jq.outerHeight(s.container) - jq.height(s.box)) / (jq.height(parent) - jq.height(d)),
    x: (jq.outerWidth(s.container) - jq.width(s.box)) / (jq.width(parent) - jq.width(d)),
  };
}
/** 先頭に戻す */
function reset(s: Instance): void {
  stopAll(s);
  const prop = s.opt.axis === 'x' ? 'left' : 'top';
  jq.setCss(s.container, prop, 0);
  jq.setCss(s.dragger, prop, 0);
}
/** 60ms ごとに中身の大きさを見て、変わったら合わせ直す（off で止める） */
function autoUpdate(s: Instance, off: boolean): void {
  const c = s.container;
  clearTimeout(c.autoUpdate);
  if (off) return;
  c.autoUpdate = window.setTimeout(() => {
    if (!s.root.isConnected) return;
    const size = s.root.scrollHeight + s.root.scrollWidth + c.offsetHeight + s.root.offsetHeight + s.root.offsetWidth;
    if (size !== s.pollSize) {
      s.pollSize = size;
      update(s);
    } else autoUpdate(s, false);
  }, 60);
}

// ---- 動かす（プラグインの _scrollTo。to は先頭からの量 px） ----

function scrollTo(s: Instance, to: number, o: { dir: 'x' | 'y'; dur?: number; easing?: string; overwrite?: string; drag?: boolean }): void {
  const { container, dragger, box } = s;
  const dur = o.dur === undefined ? s.opt.scrollInertia : o.dur;
  const easing = o.easing || 'mcsEaseOut';
  const x = o.dir === 'x';
  const prop = x ? 'left' : 'top';
  const cur = x ? container.offsetLeft : container.offsetTop;
  const limit = x ? [jq.width(box) - jq.outerWidth(container), jq.width(dragger.parentElement) - jq.width(dragger)] : [jq.height(box) - jq.outerHeight(container), jq.height(dragger.parentElement) - jq.height(dragger)];
  const ratio = x ? s.ratio.x : s.ratio.y;
  // 欄そのものがスクロールしていたら戻す（フォーカスなどでブラウザが動かしたとき。プラグインと同じ）
  if (box.scrollTop !== 0 || box.scrollLeft !== 0) {
    jq.setCss('.mCSB_' + s.idx + '_scrollbar', 'visibility', 'visible');
    box.scrollTop = 0;
    box.scrollLeft = 0;
  }
  let b = [to, to === 0 ? 0 : to / ratio];
  if (b[1] < 0 || (b[0] === 0 && b[1] === 0)) b = [0, 0];
  else if (b[1] >= limit[1]) b = [limit[0], limit[1]];
  else b[0] = -b[0];
  clearTimeout(container.onCompleteTimeout);
  tweenTo(dragger, prop, Math.round(b[1]), o.drag ? 0 : dur, easing, 'all');
  if (s.tweenRunning || !((cur === 0 && b[0] >= 0) || (cur === limit[0] && b[0] <= limit[0]))) {
    tweenTo(container, prop, Math.round(b[0]), dur, easing, o.overwrite || 'all', {
      onStart: () => {
        if (!s.tweenRunning) {
          s.tweenRunning = true;
          setDragClass(dragger, 'show');
        }
      },
      onComplete: () => {
        container.onCompleteTimeout = window.setTimeout(() => {
          s.tweenRunning = false;
          container.idleTimer = 0;
          setDragClass(dragger, 'hide');
        }, container.idleTimer || 0);
      },
    });
  }
}
/** つまみの「動いている」印（CSS の mCSB_dragger_onDrag・mCSB_scrollTools_onDrag） */
function setDragClass(d: Tweenable, mode: 'active' | 'show' | 'hide'): void {
  const tools = d.closest('.mCSB_scrollTools');
  if (mode === 'active') {
    d.classList.toggle('mCSB_dragger_onDrag');
    if (tools) tools.classList.toggle('mCSB_scrollTools_onDrag');
    d._draggable = d._draggable ? 0 : 1;
  } else if (!d._draggable) {
    d.classList[mode === 'hide' ? 'remove' : 'add']('mCSB_dragger_onDrag');
    if (tools) tools.classList[mode === 'hide' ? 'remove' : 'add']('mCSB_scrollTools_onDrag');
  }
}

/**
 * 押せるものの印（__charatOn）を、動ける間（はみ出していて止めていない）だけ付ける。前は jQuery で付けたイベントで、
 * プラグインは動けないときにイベントを外していた（確かめる道具が jQuery の記録から押せる物を数えていたので、同じに）
 */
function syncMarks(s: Instance): void {
  const on = s.bound && !s.disabled && !!s.overflowed && s.overflowed[s.opt.axis === 'x' ? 1 : 0];
  const set = (el: Element | null, types: string[]): void => {
    if (!el) return;
    (el as unknown as { __charatOn?: string[] }).__charatOn = on ? types.slice() : [];
  };
  set(s.box, ['touchstart']);
  set(s.container, ['mousedown']);
  set(s.dragger, ['mousedown', 'touchstart']);
  set(s.tools.querySelector('.mCSB_draggerContainer'), ['mousedown', 'touchstart', 'click']);
}

// ---- 押す・引っぱる・ホイール・キー ----

// ホイールの量をそろえる（jquery-mousewheel 3.1.13 と同じ。Copyright jQuery Foundation and other contributors, MIT）:
// 縦は上へ回すと正。直近（200ms）でいちばん小さい量で割った整数を目盛りの数に、いちばん小さい量を deltaFactor に
let lowestDelta = 0;
let lowestTimer = 0;
function normalizeWheel(ev: WheelEvent, el: HTMLElement): { delta: number; deltaX: number; deltaY: number; deltaFactor: number } | null {
  let deltaY = ev.deltaY * -1, deltaX = ev.deltaX, delta = deltaY;
  if (deltaY === 0) delta = deltaX * -1;
  if (deltaY === 0 && deltaX === 0) return null;
  if (ev.deltaMode === 1) {
    const parent = el.offsetParent || el;
    const lineHeight = parseInt(jq.css(parent as Element, 'fontSize'), 10) || parseInt(jq.css(el, 'fontSize'), 10) || 16;
    delta *= lineHeight;
    deltaY *= lineHeight;
    deltaX *= lineHeight;
  } else if (ev.deltaMode === 2) {
    const pageHeight = jq.height(el);
    delta *= pageHeight;
    deltaY *= pageHeight;
    deltaX *= pageHeight;
  }
  const absDelta = Math.max(Math.abs(deltaY), Math.abs(deltaX));
  if (!lowestDelta || absDelta < lowestDelta) lowestDelta = absDelta;
  const r = (v: number): number => Math[v >= 1 ? 'floor' : 'ceil'](v / lowestDelta);
  const out = { delta: r(delta), deltaX: r(deltaX), deltaY: r(deltaY), deltaFactor: lowestDelta };
  clearTimeout(lowestTimer);
  lowestTimer = window.setTimeout(() => {
    lowestDelta = 0;
  }, 200);
  return out;
}


function bindEvents(s: Instance): void {
  if (s.bound) return;
  s.bound = true;
  if ((s as Instance & { _events?: boolean })._events) return;
  (s as Instance & { _events?: boolean })._events = true;
  const { box, container, dragger, opt } = s;
  const x = opt.axis === 'x';
  const active = (): boolean => s.bound && !s.disabled && !!s.overflowed && s.overflowed[x ? 1 : 0];
  const pos = (): number => Math.abs(x ? container.offsetLeft : container.offsetTop);
  const page = (ev: MouseEvent | Touch): [number, number] => [ev.pageY, ev.pageX];

  // つまみを引っぱる（中身はあとから追いかける）
  let drag: { dy: number; dx: number } | null = null;
  const dragTo = (p: [number, number]): void => {
    const off = jq.offset(dragger)!;
    const ny = p[0] - off.top, nx = p[1] - off.left;
    container.idleTimer = opt.scrollInertia < 233 ? 250 : 0;
    const v = x ? (dragger.offsetLeft - drag!.dx + nx) * s.ratio.x : (dragger.offsetTop - drag!.dy + ny) * s.ratio.y;
    scrollTo(s, v, { dir: opt.axis, drag: true });
  };
  const startDrag = (p: [number, number]): void => {
    stopAll(s);
    const off = jq.offset(dragger)!;
    const ly = p[0] - off.top, lx = p[1] - off.left;
    const h = jq.height(dragger) + off.top, w = jq.width(dragger) + off.left;
    drag = { dy: 0, dx: 0 };
    if (h > ly && ly > 0 && w > lx && lx > 0) drag = { dy: ly, dx: lx };
    setDragClass(dragger, 'active');
  };
  dragger.addEventListener('contextmenu', (ev) => ev.preventDefault());
  dragger.addEventListener('mousedown', (ev) => {
    ev.stopImmediatePropagation();
    ev.preventDefault();
    if (!active() || ev.button !== 0) return;
    startDrag(page(ev));
  });
  dragger.addEventListener(
    'touchstart',
    (ev) => {
      ev.stopImmediatePropagation();
      if (!active()) return;
      ev.preventDefault();
      startDrag(page(ev.touches[0]));
    },
    { passive: false },
  );
  dragger.addEventListener(
    'touchmove',
    (ev) => {
      ev.stopImmediatePropagation();
      ev.preventDefault();
      if (drag) dragTo(page(ev.touches[0]));
    },
    { passive: false },
  );
  document.addEventListener('mousemove', (ev) => {
    if (drag) dragTo(page(ev));
  });
  const endDrag = (): void => {
    if (drag) {
      setDragClass(dragger, 'active');
      drag = null;
    }
  };
  document.addEventListener('mouseup', endDrag);
  document.addEventListener('touchend', endDrag);

  // レールを押す: 見えている幅（高さ）の 9 割ずつ、押した側へ
  const rail = s.tools.querySelector('.mCSB_draggerContainer') as HTMLElement;
  let railDown = false;
  rail.addEventListener('mousedown', (ev) => {
    railDown = !(ev.target as Element).classList.contains('mCSB_dragger');
  });
  rail.addEventListener('click', (ev) => {
    if (!railDown || !active()) return;
    railDown = false;
    const t = ev.target as Element;
    if (!t.classList.contains('mCSB_draggerContainer') && !t.classList.contains('mCSB_draggerRail')) return;
    stopAll(s);
    const off = jq.offset(dragger)!;
    const sign = x ? (ev.pageX > off.left ? -1 : 1) : ev.pageY > off.top ? -1 : 1;
    scrollTo(s, pos() - sign * 0.9 * (x ? jq.width(box) : jq.height(box)), { dir: opt.axis, easing: 'mcsEaseInOut' });
  });

  // ホイール（プラグインと同じ: 量を直近のいちばん小さい量で割って目盛りの数にし、1 目盛りで mouseWheelPixels。
  // いちばん小さい量が 5 未満（とても細かいタッチパッド）ならその量だけすぐ）
  box.addEventListener(
    'wheel',
    (ev) => {
      const w = normalizeWheel(ev, box);
      if (!w || !active()) return;
      stopAll(s);
      const v = x ? w.deltaY || w.delta : w.deltaY || w.delta;
      let h = opt.mouseWheelPixels, dur = opt.scrollInertia;
      const limit = x ? jq.width(dragger.parentElement) - jq.width(dragger) : jq.height(dragger.parentElement) - jq.height(dragger);
      const dpos = x ? dragger.offsetLeft : dragger.offsetTop;
      if ((v > 0 && dpos !== 0) || (v < 0 && dpos !== limit)) {
        ev.stopImmediatePropagation();
        ev.preventDefault();
      }
      if (w.deltaFactor < 5) {
        h = w.deltaFactor;
        dur = 17;
      }
      scrollTo(s, pos() - v * h, { dir: opt.axis, dur });
    },
    { passive: false },
  );

  // タッチで中身を引っぱる（指に付いて動き、離したら勢いの分だけ少し進む）
  let touch: { start: number; pos: number; last: number; lastT: number; v: number } | null = null;
  container.addEventListener(
    'touchstart',
    (ev) => {
      if (!active() || ev.touches.length > 1) return;
      stopAll(s);
      const p = x ? ev.touches[0].pageX : ev.touches[0].pageY;
      touch = { start: p, pos: pos(), last: p, lastT: now(), v: 0 };
    },
    { passive: true },
  );
  container.addEventListener(
    'touchmove',
    (ev) => {
      if (!touch || ev.touches.length > 1) return;
      const p = x ? ev.touches[0].pageX : ev.touches[0].pageY;
      const t = now();
      touch.v = (p - touch.last) / Math.max(1, t - touch.lastT);
      touch.last = p;
      touch.lastT = t;
      if (ev.cancelable) ev.preventDefault();
      scrollTo(s, touch.pos - (p - touch.start), { dir: opt.axis, dur: 0, easing: 'mcsLinearOut', drag: true });
    },
    { passive: false },
  );
  container.addEventListener('touchend', () => {
    if (!touch) return;
    const v = touch.v;
    touch = null;
    if (Math.abs(v) > 0.2) scrollTo(s, pos() - v * 200, { dir: opt.axis, dur: opt.scrollInertia + 200 });
  });

  // キー（欄にフォーカスがあるとき）
  box.setAttribute('tabindex', '0');
  box.addEventListener('keydown', (ev) => {
    if (!active()) return;
    const k = ev.keyCode;
    const back = x ? 37 : 38, fwd = x ? 39 : 40;
    if (k === back || k === fwd) {
      ev.preventDefault();
      scrollTo(s, pos() + (k === fwd ? 40 : -40), { dir: opt.axis, dur: 17, easing: 'linear' });
    } else if (k === 33 || k === 34) {
      ev.preventDefault();
      scrollTo(s, pos() + (k === 34 ? 1 : -1) * 0.9 * (x ? jq.width(box) : jq.height(box)), { dir: opt.axis, easing: 'mcsEaseInOut' });
    } else if (k === 35 || k === 36) {
      ev.preventDefault();
      const end = Math.abs(x ? jq.width(box) - jq.outerWidth(container) : jq.height(box) - jq.outerHeight(container));
      scrollTo(s, k === 35 ? end : 0, { dir: opt.axis, easing: 'mcsEaseInOut' });
    }
  });
}

// ---- 外から（fx.ts） ----

const each = (sel: string, fn: (s: Instance) => void): void => {
  for (const el of jq.els(sel)) {
    const s = instances.get(el);
    if (s) fn(s);
  }
};
/** 止める（プラグインの "disable"。reset なら先頭に戻す） */
export function disable(sel: string, resetPos: boolean): void {
  each(sel, (s) => {
    autoUpdate(s, true);
    s.bound = false;
    s.disabled = true;
    if (resetPos) reset(s);
    showScrollbar(s, true);
    s.root.classList.add('mCS_disabled');
    syncMarks(s);
  });
}
/** 合わせ直す（プラグインの "update"） */
export function refresh(sel: string): void {
  each(sel, (s) => update(s));
}
/** 動かす（プラグインの "scrollTo"。to は [縦, 横]、60ms 待ってから inertia ms で） */
export function scrollToPos(sel: string, to: [number | null, number | null], inertia: number): void {
  each(sel, (s) => {
    const dur = inertia > 0 && inertia < 17 ? 17 : inertia;
    const y = to[0] == null ? null : Math.abs(to[0]), xv = to[1] == null ? null : Math.abs(to[1]);
    setTimeout(() => {
      if (y !== null && s.opt.axis !== 'x' && s.overflowed && s.overflowed[0]) scrollTo(s, y, { dir: 'y', dur, easing: 'mcsEaseInOut', overwrite: 'all' });
      if (xv !== null && s.opt.axis !== 'y' && s.overflowed && s.overflowed[1]) scrollTo(s, xv, { dir: 'x', dur, easing: 'mcsEaseInOut', overwrite: 'none' });
    }, 60);
  });
}
